/**
 * Members — the role-grouped member list. Every action/detail (a row click,
 * "New role", "Invite") opens through the same show() overlay at every
 * viewport — see web's members.view.ts, which owns the single reconciler.
 * This view renders the list only.
 */
import { fill, overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html, type TemplateResult } from "lit";
import { choose } from "lit/directives/choose.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { activeActor } from "../auth/actor.state.ts";
import { LocalizeMixin } from "../i18n/localize.ts";
import {
	detailState,
	grouped,
	inviteDraft,
	memberListState,
} from "./members.state.ts";

// Role-group cards fill available desktop width with as many columns as fit
// (auto-fit) and cap each card's width at 24rem so row anatomy — avatar,
// name/email, chevron — stays dense and legible rather than stretching into
// a wide ribbon; below 20rem+gutter the track count collapses to one,
// giving mobile the same single column it already had. One shared constant
// so the "populated" grid and its "loading" skeleton mirror the identical
// track geometry.
const ROLE_CARD_GRID =
	"grid grid-cols-[repeat(auto-fit,minmax(20rem,24rem))] gap-4 px-4 pt-4";

@schmancyElement("members-list")
export class Members extends LocalizeMixin(SchmancyElement) {
	override render(): TemplateResult {
		const detail = detailState.value;
		const actor = activeActor.get();
		const canInvite =
			actor?.permissions.includes("tickets.members.invite") ?? false;

		return html`
			<section ${fill()} class="flex flex-col">
				<members-header class="flex-none"></members-header>

				<div ${overflowWithin()} class="min-w-0 pb-4">
					${choose(memberListState.get(), [
						[
							"loading",
							() => html`
								<div
									class=${ROLE_CARD_GRID}
									role="status"
									aria-busy="true"
									aria-label=${this.t("admin.members.list.loadingAriaLabel")}
								>
									${repeat(
										[0, 1] as const,
										(i) => i,
										() => html`
											<schmancy-surface type="containerLow" class="rounded-2xl p-1.5 flex flex-col gap-1">
												<div class="flex items-center gap-3 rounded-xl px-4 py-3">
													<schmancy-skeleton shape="text" class="w-2/5 h-4"></schmancy-skeleton>
												</div>
												${repeat(
													[0, 1] as const,
													(j) => j,
													() => html`
														<div class="flex items-center gap-3 rounded-xl px-4 py-3">
															<schmancy-skeleton shape="circle" class="w-10 h-10 shrink-0"></schmancy-skeleton>
															<div class="flex flex-col gap-1.5 flex-1 min-w-0">
																<schmancy-skeleton shape="text" class="w-2/5"></schmancy-skeleton>
																<schmancy-skeleton shape="text" class="w-3/5"></schmancy-skeleton>
															</div>
														</div>
													`,
												)}
											</schmancy-surface>
										`,
									)}
								</div>
							`,
						],
						[
							"error",
							() => html`
								<div class="flex flex-col items-start gap-3 px-6 py-8">
									<schmancy-icon class="text-3xl text-error-default">error</schmancy-icon>
									<schmancy-typography type="title" token="sm">${this.t("admin.members.list.errorTitle")}</schmancy-typography>
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
										${this.t("admin.members.list.errorBody")}
									</schmancy-typography>
								</div>
							`,
						],
						[
							"denied",
							() => html`
								<div class="flex flex-col items-start gap-3 px-6 py-8">
									<schmancy-icon class="text-3xl text-error-default">lock</schmancy-icon>
									<schmancy-typography type="title" token="sm">${this.t("admin.members.list.deniedTitle")}</schmancy-typography>
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
										${this.t("admin.members.list.deniedBody")}
									</schmancy-typography>
								</div>
							`,
						],
						[
							"empty",
							() => html`
								<div class="flex flex-col items-start gap-3 px-6 py-8">
									<schmancy-icon class="text-3xl text-surface-onVariant">group_add</schmancy-icon>
									<schmancy-typography type="title" token="sm">${this.t("admin.members.list.emptyTitle")}</schmancy-typography>
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
										${this.t(
											canInvite
												? "admin.members.list.emptyBodyCanInvite"
												: "admin.members.list.emptyBodyCannotInvite",
										)}
									</schmancy-typography>
									${when(
										canInvite,
										() => html`
											<schmancy-button
												variant="filled"
												@click=${() => {
													inviteDraft.replace({
														email: "",
														displayName: "",
														// No pre-picked role — an admin default here would let a
														// send go out before anyone chose who gets what authority.
														roleId: "",
														scope: {
															kind: "all-events",
															orgId: activeActor.get()?.orgId ?? "",
														},
													});
													detailState.replace({ kind: "invite" });
												}}
											>
												<schmancy-icon slot="prefix" aria-hidden="true">person_add</schmancy-icon>
												${this.t("admin.members.list.inviteSomeone")}
											</schmancy-button>
										`,
									)}
								</div>
							`,
						],
						[
							"populated",
							() => html`
								<div class=${`${ROLE_CARD_GRID} items-start`}>
									${repeat(
										grouped.get(),
										(g) => g.role.id,
										(g) => html`
											<schmancy-surface type="containerLow" class="rounded-2xl p-1.5">
												<schmancy-list surface="transparent">
													<schmancy-list-item
														rounded
														?selected=${detail.kind === "role" && detail.id === g.role.id}
														aria-label=${this.t(
															g.total === 1
																? "admin.members.list.roleGroupAriaLabelOne"
																: "admin.members.list.roleGroupAriaLabelOther",
															{ role: g.role.name, count: g.total },
														)}
														@click=${() => detailState.replace({ kind: "role", id: g.role.id })}
													>
														<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
															${g.role.name} · ${g.total}
														</schmancy-typography>
														<schmancy-icon slot="trailing" class="text-surface-onVariant text-base" aria-hidden="true"
															>chevron_right</schmancy-icon
														>
													</schmancy-list-item>
													${repeat(
														g.members,
														(m) => m.uid,
														(m) => html`
															<schmancy-list-item
																rounded
																?selected=${detail.kind === "member" && detail.uid === m.uid}
																aria-label=${m.label}
																@click=${() => detailState.replace({ kind: "member", uid: m.uid })}
															>
																<schmancy-avatar slot="leading" class="w-10 h-10" .initials=${m.initials}></schmancy-avatar>
																<div class="min-w-0 flex-1">
																	<schmancy-typography type="title" token="sm" class="truncate">${m.label}</schmancy-typography>
																	<schmancy-typography type="body" token="sm" class="text-surface-onVariant block truncate">
																		${m.email}
																	</schmancy-typography>
																</div>
																<schmancy-icon slot="trailing" aria-hidden="true">chevron_right</schmancy-icon>
															</schmancy-list-item>
														`,
													)}
													${repeat(
														g.invites,
														(i) => i.token,
														(i) => html`
															<schmancy-list-item
																rounded
																?selected=${detail.kind === "invite-detail" && detail.token === i.token}
																aria-label=${this.t("admin.members.list.pendingInviteAriaLabel", { name: i.displayName })}
																@click=${() => detailState.replace({ kind: "invite-detail", token: i.token })}
															>
																<schmancy-avatar
																	slot="leading"
																	class="w-10 h-10"
																	color="neutral"
																	icon="schedule"
																	aria-hidden="true"
																></schmancy-avatar>
																<div class="min-w-0 flex-1">
																	<div class="flex items-center gap-1.5">
																		<schmancy-typography type="title" token="sm" class="truncate">${i.displayName}</schmancy-typography>
																		<schmancy-typography type="label" token="xs" class="text-warning-default shrink-0">${this.t("admin.members.list.pendingBadge")}</schmancy-typography>
																	</div>
																	<schmancy-typography type="body" token="sm" class="text-surface-onVariant block truncate">
																		${i.email}
																	</schmancy-typography>
																</div>
																<schmancy-icon slot="trailing" aria-hidden="true">chevron_right</schmancy-icon>
															</schmancy-list-item>
														`,
													)}
												</schmancy-list>
											</schmancy-surface>
										`,
									)}
								</div>
							`,
						],
					])}
				</div>
			</section>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"members-list": Members;
	}
}
